import type { Md5WorkerRequest, Md5WorkerResponse } from './md5Worker';

const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB
const MAX_WORKERS = 4;

const abortError = () => {
  const error = new Error('MD5 计算已取消');
  error.name = 'AbortError';
  return error;
};

type Md5WorkerLike = {
  postMessage: {
    (message: Md5WorkerRequest, transfer: Transferable[]): void;
    (message: Md5WorkerRequest, options?: StructuredSerializeOptions): void;
  };
  terminate: () => void;
  onmessage: ((event: MessageEvent<Md5WorkerResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
};

type Task = {
  file: File;
  signal?: AbortSignal;
  resolve: (md5: string) => void;
  reject: (error: unknown) => void;
};

const createWorker = (): Md5WorkerLike | null => {
  try {
    return new Worker(new URL('./md5Worker.ts', import.meta.url), {
      type: 'module',
    });
  } catch {
    return null;
  }
};

class Md5WorkerClient {
  private worker: Md5WorkerLike | null;
  private taskId = 0;
  private onTaskEnd: (client: Md5WorkerClient) => void;

  constructor(onTaskEnd: (client: Md5WorkerClient) => void) {
    this.onTaskEnd = onTaskEnd;
    this.worker = createWorker();
  }

  get hasWorker() {
    return this.worker !== null;
  }

  compute(file: File, signal?: AbortSignal): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const worker = this.worker;
      if (!worker) {
        reject(new Error('Worker 创建失败'));
        this.onTaskEnd(this);
        return;
      }

      const taskId = `t${this.taskId++}`;
      let settled = false;
      let offset = 0;
      let cancelled = false;

      const finish = (error?: unknown, md5?: string) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', handleAbort);
        if (error) {
          reject(error);
        } else {
          resolve(md5!);
        }
        this.onTaskEnd(this);
      };

      const handleAbort = () => {
        if (settled) return;
        cancelled = true;
        worker.postMessage({ type: 'cancel', taskId });
        finish(abortError());
      };

      signal?.addEventListener('abort', handleAbort, { once: true });

      worker.onmessage = (event: MessageEvent<Md5WorkerResponse>) => {
        const msg = event.data;
        if (msg.taskId !== taskId) return;
        if (msg.type === 'result') {
          finish(undefined, msg.md5);
          return;
        }
        if (msg.type === 'error') {
          finish(new Error(msg.message || 'MD5 计算失败'));
        }
      };

      worker.onerror = (event) => {
        finish(new Error(event.message || 'MD5 计算失败'));
      };

      const readNextChunk = async () => {
        while (!cancelled && !settled) {
          if (offset >= file.size) {
            worker.postMessage({
              type: 'chunk',
              taskId,
              chunk: new ArrayBuffer(0),
              isLast: true,
            });
            return;
          }
          const end = Math.min(offset + CHUNK_SIZE, file.size);
          const buffer = await file.slice(offset, end).arrayBuffer();
          if (cancelled || settled) return;
          offset = end;
          worker.postMessage({ type: 'chunk', taskId, chunk: buffer }, [buffer]);
        }
      };

      worker.postMessage({ type: 'init', taskId });
      void readNextChunk();
    });
  }

  terminate() {
    this.worker?.terminate();
    this.worker = null;
  }
}

const queue: Task[] = [];
const activeClients = new Set<Md5WorkerClient>();
const idleClients: Md5WorkerClient[] = [];

const runNextTask = () => {
  if (!queue.length) return;
  const client =
    idleClients.pop() ??
    (activeClients.size < MAX_WORKERS ? new Md5WorkerClient(onClientIdle) : undefined);
  if (!client) return;
  const task = queue.shift()!;

  if (!client.hasWorker) {
    task.reject(new Error('Worker 创建失败'));
    return;
  }

  activeClients.add(client);
  client.compute(task.file, task.signal).then(
    (md5) => task.resolve(md5),
    (error) => task.reject(error)
  );
};

const onClientIdle = (client: Md5WorkerClient) => {
  activeClients.delete(client);
  if (client.hasWorker) {
    idleClients.push(client);
  }
  runNextTask();
};

const enqueueCompute = (file: File, signal?: AbortSignal): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const task: Task = {
      file,
      signal,
      resolve: (md5) => {
        signal?.removeEventListener('abort', handleEnqueueAbort);
        resolve(md5);
      },
      reject: (error) => {
        signal?.removeEventListener('abort', handleEnqueueAbort);
        reject(error);
      },
    };
    const handleEnqueueAbort = () => {
      const index = queue.indexOf(task);
      if (index >= 0) {
        queue.splice(index, 1);
        reject(abortError());
      }
    };
    signal?.addEventListener('abort', handleEnqueueAbort, { once: true });
    queue.push(task);
    runNextTask();
  });
};

const isWorkerSupported = () => {
  try {
    return (
      typeof Worker !== 'undefined' &&
      typeof URL?.createObjectURL === 'function'
    );
  } catch {
    return false;
  }
};

export const computeFileMd5 = async (file: File, signal?: AbortSignal): Promise<string> => {
  if (signal?.aborted) throw abortError();
  if (!isWorkerSupported()) {
    throw new Error('当前环境不支持 Web Worker');
  }
  return enqueueCompute(file, signal);
};

export const disposeMd5Workers = () => {
  const pending = queue.splice(0, queue.length);
  pending.forEach((task) => task.reject(abortError()));
  idleClients.forEach((client) => client.terminate());
  idleClients.length = 0;
  activeClients.forEach((client) => client.terminate());
  activeClients.clear();
};
